import { useCallback, useMemo } from "react";
import SparketModal from "components/common/modal/modal.tsx";
import { useAppDispatch, useAppSelector } from "state/hooks.ts";
import {
  getDepositFundsModalState,
  hideDepositModalV2,
  setSelectedAmount,
} from "state/slices/deposit-funds-modal-v2-slice.ts";
import { showGlobalLoadingOverlay, hideGlobalLoadingOverlay } from "state/slices/global-loading-overlay-slice.ts";
import { fetchRealWallet } from "state/slices/user-real-wallet-slice.ts";
import { fetchOrders } from "state/slices/user-orders-slice.ts";
import { postRequest } from "utils/httpClient.ts";
import { DepositFundsRequest } from "interfaces/commerce/order/create-order-request.ts";
import { Order } from "interfaces/commerce/order/order.ts";
import { ErrorResponseBody } from "interfaces/error-response-body.ts";
import { ErrorCodes } from "interfaces/error-codes.ts";
import { toast } from "react-toastify";
import GreyModalSection from "components/commerce/grey-modal-section.tsx";
import BalanceDisplay from "components/common/real-money-wallet/balance-display.tsx";
import DepositFundsModalPaymentMethodsBlock from "./deposit-funds-modal-payment-method-block.tsx";
import DepositButton from "./deposit-button.tsx";
import AmountSelector from "../amount-selector.tsx";
import DepositNote from "./deposit-note.tsx";
import { getOrdersPath } from "utils/backend-path-builders.ts";
import { ACH_MINIMUM_DEPOSIT } from "utils/constants.ts";

const DepositFundsModal = () => {
  const dispatch = useAppDispatch();
  const modalState = useAppSelector(getDepositFundsModalState);

  const handleClose = useCallback(() => {
    dispatch(hideDepositModalV2());
  }, [dispatch]);

  const handleAmountChange = useCallback((amount: number) => {
    dispatch(setSelectedAmount(amount));
  }, [dispatch]);

  const createOrder = useCallback(async (): Promise<Order> => {
    const { selectedAmount, selectedPaymentMethod } = modalState;

    if (!selectedPaymentMethod || !selectedAmount) {
      throw new Error('Missing payment method or amount');
    }

    const request: DepositFundsRequest = {
      amount: selectedAmount,
      orderType: "DEPOSIT_FUNDS",
      successPathname: window.location.pathname
    };

    // Add payment method specific fields
    if (selectedPaymentMethod.type === 'CARD') {
      // For CARD payments, only add userPaymentMethodId (inovio) if it's not 'NEW'
      if (selectedPaymentMethod.inovioUserPaymentMethodId !== 'NEW') {
        request.userPaymentMethodId = selectedPaymentMethod.inovioUserPaymentMethodId;
      }
      // If it's 'NEW', no userPaymentMethodId means hosted payment page
    } else if (selectedPaymentMethod.type === 'ACH') {
      request.paynoteFundingSourceId = selectedPaymentMethod.paynoteFundingSourceId;
    }

    const response = await postRequest(getOrdersPath(), { body: request });
    return response as Order;
  }, [modalState]);

  const handleDirectPayment = useCallback(async () => {
    dispatch(showGlobalLoadingOverlay({ message: "Processing payment..." }));

    try {
      const order = await createOrder();
      dispatch(fetchOrders());

      if (order.status === 'COMPLETED') {
        dispatch(fetchRealWallet());
        handleClose();
      } else if (order.status === 'PROCESSING') {
        toast.info('Deposit is being processed...');
        dispatch(fetchRealWallet());
        handleClose();
      } else if (order.status === 'PAYMENT_FAILED') {
        toast.error('Payment failed. Please try a different payment method or contact support.');
        // Don't close modal - let user try again
      } else if (order.status === 'ERROR') {
        toast.error('An error occurred processing your payment. Please contact support.');
        // Don't close modal - let user try again
      } else {
        // Handle any other unexpected statuses
        toast.warning(`Order status: ${order.status}. Please check your orders or contact support.`);
        handleClose();
      }
    } catch (error: any) {
      const errorResponse: ErrorResponseBody | undefined = error.response?.data;
      if (errorResponse) {
        const error_code = errorResponse.error_code;
        switch (error_code) {
          case ErrorCodes.DepositLimitationExceeded:
            toast.error(errorResponse.friendly_error_message);
            break;
          default:
            toast.error("Something went wrong. Please contact support.");
        }
      } else {
        toast.error("Something went wrong. Please contact support.");
      }
    } finally {
      dispatch(hideGlobalLoadingOverlay());
    }
  }, [createOrder, dispatch, handleClose]);

  const handleHostedPaymentRedirect = useCallback(async () => {
    dispatch(showGlobalLoadingOverlay({ message: "Preparing payment page..." }));

    try {
      const order = await createOrder();

      if (order.payment_page_url) {
        window.location.href = order.payment_page_url;
        // Don't reset processing state since user is leaving the page
      } else {
        throw new Error('No payment page URL received from server');
      }
    } catch (error: any) {
      const errorResponse: ErrorResponseBody | undefined = error.response?.data;
      if (errorResponse) {
        const error_code = errorResponse.error_code;
        switch (error_code) {
          case ErrorCodes.DepositLimitationExceeded:
            toast.error(errorResponse.friendly_error_message);
            break;
          default:
            toast.error("Failed to prepare payment page. Please contact support.");
        }
      } else {
        toast.error("Failed to prepare payment page. Please contact support.");
      }
      dispatch(hideGlobalLoadingOverlay());
    }
  }, [createOrder, dispatch]);

  // Determine payment flow based on selected payment method
  const shouldUseInovioHostedPaymentPage =
    modalState.selectedPaymentMethod?.type === 'CARD' &&
    modalState.selectedPaymentMethod.inovioUserPaymentMethodId === 'NEW';

  const handleDeposit = shouldUseInovioHostedPaymentPage ? handleHostedPaymentRedirect : handleDirectPayment;

  // Create amount options with disabled state based on payment method
  const amountOptions = useMemo(() => {
    const baseAmounts = [5, 20, 100];
    const isACHSelected = modalState.paymentMethodType === 'ACH';

    return baseAmounts.map(amount => ({
      amount,
      disabled: isACHSelected && amount < ACH_MINIMUM_DEPOSIT
    }));
  }, [modalState.paymentMethodType]);

  return (
    <SparketModal title="Deposit Funds" show={modalState.show} onClose={handleClose}>
      <div style={{ position: "relative" }}>
        {modalState.processing && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              zIndex: 10,
            }}
          />
        )}

        <GreyModalSection>
          <BalanceDisplay />
        </GreyModalSection>

        <GreyModalSection>
          <AmountSelector
            presetAmounts={amountOptions}
            selectedAmount={modalState.selectedAmount}
            setSelectedAmount={handleAmountChange}
            placeholder="$ other amount"
          />
        </GreyModalSection>

        <GreyModalSection>
          <DepositFundsModalPaymentMethodsBlock />
        </GreyModalSection>

        <div className="d-flex flex-column align-items-center">
          <DepositButton onDeposit={handleDeposit} />
          <DepositNote />
        </div>
      </div>
    </SparketModal>
  );
};

export default DepositFundsModal; 